import type { Task } from "@app/core";
import { isAncestor } from "@app/core";
import { useRef } from "react";
import { useInsertPromptStore, useNestHoverStore, useTasksStore } from "../../stores/index.js";
import { getDragTaskId, splitVirtualId } from "./drag.js";

const DWELL_MS = 500;

/**
 * Drop a task onto another task's block/chip to make it a subtask. Two ways to
 * arm a nest (so plain drags stay reschedules and need no popup):
 *  - hold **Shift** while dropping, or
 *  - **dwell** ~0.5s over the target's TITLE (the `[data-block-title]` element)
 *    without releasing — hovering elsewhere on the block never arms, so a drag
 *    passing across a block's body stays a plain reschedule (the target
 *    animates via {@link useNestHoverStore} `armedId`); release while armed to
 *    nest.
 * Either way the dropped task is nested inside the target (its time cleared).
 * No modifier + no dwell: the event bubbles so the grid reschedules. A subtask
 * dropped back on its OWN parent additionally offers the transient "Insert" pill.
 * Cycles and recurring sources/targets are rejected (validated on drop, since the
 * drag id isn't readable during dragover).
 */
export function useNestDrop() {
  const nestUnder = useTasksStore((s) => s.nestUnder);
  const offerInsert = useInsertPromptStore((s) => s.offer);
  const arm = useNestHoverStore((s) => s.arm);
  const disarm = useNestHoverStore((s) => s.disarm);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const canTarget = (target: Task) => !target.virtual && !target.recurrence_id;

  function clearDwell() {
    if (timer.current) { clearTimeout(timer.current); timer.current = undefined; }
  }

  function onDragOver(e: React.DragEvent, target: Task) {
    if (!canTarget(target)) return;
    e.preventDefault();
    // Nest intent = hovering the target's TITLE row, not just anywhere on it.
    // The band is the title's vertical extent across the block's full width; a
    // missing/empty title (zero-height rect) falls back to the block's top
    // strip so title-less entries can still receive subtasks.
    const host = e.currentTarget as HTMLElement;
    const hostRect = host.getBoundingClientRect();
    const titleRect = host.querySelector("[data-block-title]")?.getBoundingClientRect();
    const band = titleRect && titleRect.height >= 8
      ? { top: titleRect.top, bottom: titleRect.bottom }
      : { top: hostRect.top, bottom: hostRect.top + Math.min(28, hostRect.height) };
    const overTitle = e.clientY >= band.top && e.clientY <= band.bottom
      && e.clientX >= hostRect.left && e.clientX <= hostRect.right;
    if (!overTitle) {
      clearDwell();
      if (useNestHoverStore.getState().armedId === target.id) disarm();
      return;
    }
    if (!timer.current) timer.current = setTimeout(() => { arm(target.id); timer.current = undefined; }, DWELL_MS);
  }

  function onDragLeave(e: React.DragEvent) {
    // Ignore leave events fired while moving onto a descendant element.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    clearDwell();
    disarm();
  }

  function onDrop(e: React.DragEvent, target: Task) {
    clearDwell();
    const armed = useNestHoverStore.getState().armedId === target.id;
    disarm();
    const dropped = getDragTaskId(e);
    if (!dropped || !canTarget(target)) return;
    const { sourceId } = splitVirtualId(dropped);
    if (!sourceId || sourceId === target.id) return;
    const allTasks = useTasksStore.getState().tasks;
    const src = allTasks.find((t) => t.id === sourceId);
    if (!src || src.recurrence_id || isAncestor(allTasks, sourceId, target.id)) return;

    if (armed || e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      void nestUnder(sourceId, target.id); // nest inside the target
      return;
    }
    // No modifier / no dwell: let the grid reschedule. Offer to re-insert a
    // subtask that landed back on its own parent.
    if (src.subtask_parent_id === target.id) offerInsert(sourceId, target.id);
  }

  return { onDragOver, onDragLeave, onDrop };
}
